import { useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { SelectionProvider } from "@/contexts/SelectionContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ReauthProvider } from "@/contexts/ReauthContext";
import { queryClient } from "@/lib/queryClient";
import { trackPageView } from "@/lib/analytics";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Auth from "./pages/Auth";
import GoogleCallback from "./pages/GoogleCallback";
import Settings from "./pages/Settings";
import GmailSetup from "./pages/GmailSetup";
import FeedHealthDashboard from "./pages/FeedHealthDashboard";
import PerformanceDashboard from "./pages/PerformanceDashboard";
import Pricing from "./pages/Pricing";
import Roadmap from "./pages/Roadmap";
import Workbench from "./pages/Workbench";
import NotFound from "./pages/NotFound";
import CaseStudyConsent from "./pages/CaseStudyConsent";
import OptimizationModalPreview from "./pages/dev/OptimizationModalPreview";
import AdsPerformance from "./pages/AdsPerformance";
import AdsSteuerung from "./pages/AdsSteuerung";
import EntwurfWerkbank from "./pages/EntwurfWerkbank";
import { CommandPalette } from "@/components/CommandPalette";
import { RedirectPreservingQuery } from "@/components/RedirectPreservingQuery";
import { FloatingFeedbackButton } from "@/components/roadmap/FloatingFeedbackButton";
import { useJobCompletionNotifications } from "@/hooks/useJobCompletionNotifications";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function JobCompletionNotificationBridge() {
  useJobCompletionNotifications();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="system" storageKey="feed-optimizer-theme" attribute="class">
      <AuthProvider>
        <SelectionProvider>
          <NotificationProvider>
            <ReauthProvider>
            <TooltipProvider>
            <JobCompletionNotificationBridge />
            {/* UX-Audit A3-NEU-2, G5-20, G2-15 (Dennis-Entscheidung 13.10.2026,
                Option 2B): oben mittig UNTER der Kopfleiste. Oben rechts lagen
                Toasts ueber Glocke, Theme- und Konto-Knopf und ueber dem Knopf
                des Kapazitaetsbanners. 64 px = Kopfleiste `h-14` (56 px) aus
                Layout.tsx plus 8 px Luft; auf dem Handy legt Sonner Toasts ueber
                die volle Breite, deshalb gilt derselbe Abstand auch dort.
                Bekannte Grenzen: (1) steht ein Banner ueber der Kopfleiste
                (GoogleAuthRevokedBanner, AccessRevokedBanner), rutscht die
                Kopfleiste nach unten und der Toast liegt wieder auf ihr.
                (2) Unter 600 px ist der Toast vollbreit und liegt fuer seine
                Anzeigedauer ueber einem sichtbaren Kapazitaetsbanner, das dort
                direkt unter der Kopfleiste Text und Knopf stapelt; in der
                WP6-E2E bei 390 px nachmessen. */}
            <Sonner position="top-center" duration={4000} offset={{ top: 64 }} mobileOffset={{ top: 64 }} />
            <BrowserRouter>
              <RouteTracker />
              <CommandPalette />
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                {/* Der Feed-Check ist die Startseite (Task 13). SimpleHome ist
                    aufgeloest, es bestand zuletzt aus Seitenkopf, CardGridLayout
                    und den Dialogen, die jetzt hier haengen. */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout>
                      <FeedHealthDashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* Alt-Adresse: steht in der Report-Mail
                    (send-email/templates/transactional.ts) und in Links im
                    Umlauf. Der Query-String muss mit, `feedcheck=1` ist die
                    Quelle der Trichter-Kennzahl. */}
                <Route path="/optimization-score" element={<RedirectPreservingQuery to="/" />} />
                {/* Task 15: die zweite Produktdetailflaeche ist entfallen. Sie
                    hatte eine eigene Vor-Zurueck-Navigation zwischen Produkten
                    und wurde aus der Produktliste erreicht, die seit Task 10 in
                    der Werkbank liegt. Ihr Mehrwert, der Score-Aufbau, ist der
                    dritte Bereich der Werkbank-Karte. Verloren geht nur die
                    Vorauswahl des einen Produkts, nicht die Information; alte
                    Links dieser Form sind selten. Weiterleitung mit
                    Query-String, damit eine angehaengte Kennzahl nicht still
                    verschwindet. */}
                <Route path="/optimization-score/:productId" element={<RedirectPreservingQuery to="/workbench" />} />
                <Route path="/performance" element={
                  <ProtectedRoute>
                    <Layout>
                      <PerformanceDashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/pricing" element={
                  <ProtectedRoute>
                    <Layout>
                      <Pricing />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/roadmap" element={
                  <ProtectedRoute>
                    <Layout>
                      <Roadmap />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/workbench" element={
                  <ProtectedRoute>
                    <Layout>
                      <Workbench />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* Redirect old standalone pages to Settings tabs */}
                <Route path="/google-accounts" element={<Navigate to="/settings?tab=accounts" replace />} />
                <Route path="/merchant-centers" element={<Navigate to="/settings?tab=merchants" replace />} />
                <Route path="/feeds" element={<Navigate to="/settings?tab=feeds" replace />} />
                {/* `/schedules` rendert schon vor Task 13 exakt dieselbe
                    Komponente wie der Einstellungs-Tab. */}
                <Route path="/schedules" element={<RedirectPreservingQuery to="/settings?tab=schedules" />} />
                <Route path="/admin/gmail-setup" element={<GmailSetup />} />
                <Route path="/case-study-consent" element={<CaseStudyConsent />} />
                {import.meta.env.DEV && (
                  <Route path="/dev/optimization-modal-preview" element={<OptimizationModalPreview />} />
                )}
                {/* Google-Ads-Lesepfad (Spec AP4), NICHT VERLINKT (harte Grenze
                    G1). Die Route ist per URL erreichbar und taucht bewusst
                    weder in `AppSidebar.tsx` noch in `commandPaletteItems.ts`
                    auf: der Scope `auth/adwords` ist bei Google noch nicht
                    freigegeben, und "do not deploy
                    unverified scopes to your production traffic" ist Googles
                    eigene Formulierung.

                    Ausdruecklich OHNE `import.meta.env.DEV`-Gate, anders als
                    die Vorschau darueber. Das Demo-Video fuer die
                    Scope-Freigabe muss diesen Flow in der DEPLOYTEN App zeigen;
                    ein DEV-Gate macht ihn unfilmbar. Der Schutz ist, dass die
                    Route unverlinkt ist, nicht dass sie fehlt.

                    Gepinnt von `src/test/adsScopeIsolation.test.ts`. */}
                <Route path="/internal/ads-performance" element={
                  <ProtectedRoute>
                    <Layout>
                      <AdsPerformance />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* Ads-Steuerungs-Modul, Scheibe 1. Dieselbe harte Grenze G1
                    wie die Zeile darueber: per URL erreichbar, in keiner
                    Navigation, KEIN `import.meta.env.DEV`-Gate. Zusaetzlicher
                    Grund hier: die Flaeche traegt die Schreibwege des
                    Custom-Label-Rollouts (Tier uebernehmen, Labels
                    hochladen). Sie bedient ausschliesslich das eigene Team,
                    und ein Kunde, der versehentlich hineinlaeuft, saehe die
                    Rollout-Liste anderer Konten.

                    Gepinnt von `src/test/adsScopeIsolation.test.ts`. */}
                <Route path="/internal/ads-steuerung" element={
                  <ProtectedRoute>
                    <Layout>
                      <AdsSteuerung />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* Der Werkbank-Umbau als schaltbarer Entwurf. Dieselbe
                    harte Grenze wie die beiden Routen darueber: per URL
                    erreichbar, in keiner Navigation, KEIN
                    `import.meta.env.DEV`-Gate - der Sinn der Seite ist ja
                    gerade, den Entwurf in der DEPLOYTEN App mit echten
                    Kundendaten zu beurteilen.

                    Sie rendert `Workbench` unveraendert und legt nur andere
                    Optionen darueber (`src/entwurf/EntwurfOptionen.tsx`).
                    Ohne Provider gilt ueberall das heutige Verhalten, diese
                    Route kann `/workbench` also nicht beeinflussen.

                    MUSS in FLUID_ROUTES stehen (Layout.tsx), sonst reicht die
                    Hoehenkette nicht durch und die Karte kollabiert. */}
                <Route path="/internal/entwurf-werkbank" element={
                  <ProtectedRoute>
                    <Layout>
                      <EntwurfWerkbank />
                    </Layout>
                  </ProtectedRoute>
                } />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              {/* Nach den Routen, damit der Knopf nicht der erste Tab-Stopp jeder Seite ist (A3-21). */}
              <FloatingFeedbackButton />
            </BrowserRouter>
            </TooltipProvider>
            </ReauthProvider>
          </NotificationProvider>
        </SelectionProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
